import { Box } from '@mui/joy';
import { t } from 'i18next';
import { upperCase } from 'lodash';

import { ICompanySettings } from 'auth/types';
import { TextEllipsis, Total, TotalPrice } from 'core/elements';
import { NumberHelper } from 'core/helpers';
import { getPercentage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { FreightStatus } from 'quotes/enums';
import { IQuoteVersionData } from 'quotes/types';
import { Level } from 'theme/enums';

import { FreightPriceType } from './enums';

export const getFreightRow = (
  quoteVersionData: IQuoteVersionData,
  options: {
    classNames?: string;
    freightSettings?: Pick<ICompanySettings, 'defaultFreightPercentage' | 'minimumFreightCharge'>;
  } = {}
) => {
  const { classNames = '', freightSettings = {} } = options;

  if (!quoteVersionData) {
    return [];
  }

  const percentageWarning =
    NumberHelper.compare(
      quoteVersionData.freightPercentage,
      freightSettings.defaultFreightPercentage
    ) < 0
      ? t('errorFreightPercentageBellowMinimum', {
          minimum: getPercentage(freightSettings.defaultFreightPercentage),
        })
      : '';

  const typeElement = (
    <Total
      key="freightType"
      label={t('labelFreightType')}
      value={upperCase(quoteVersionData.freightStatus)}
      options={{ classNames }}
    />
  );

  const noteElement = quoteVersionData.freightNote && (
    <Box className={classNames} key="freightNote">
      {t('labelFreightNote')}:
      <TextEllipsis
        content={quoteVersionData.freightNote}
        sx={{ paddingLeft: '4px', maxWidth: '300px' }}
      />
    </Box>
  );

  const costElement = (
    <TotalPrice
      key="freightCost"
      label={t('labelFreightCost')}
      value={quoteVersionData.freightCost}
      options={{ classNames, valueProps: { level: Level.BODY_SM } }}
    />
  );

  const marginElement = (
    <TotalPrice
      key="freightMargin"
      label={t('labelFreightMargin')}
      value={PricingHelper.computeGpm(quoteVersionData.freightCost, quoteVersionData.freightPrice)}
      options={{ classNames, isPercentage: true }}
    />
  );

  const percentageElement = (
    <TotalPrice
      key="freightPercentage"
      label={t('labelFreightPercentage')}
      value={quoteVersionData.freightPercentage}
      options={{
        classNames,
        isPercentage: true,
        warning: percentageWarning,
      }}
    />
  );

  switch (quoteVersionData.freightStatus) {
    case FreightStatus.OMITTED:
      return [typeElement, noteElement];
    case FreightStatus.PREPAID_AND_CHARGE:
      return [typeElement];
    case FreightStatus.SPREAD:
      return [typeElement, percentageElement];
    case FreightStatus.COMBINED:
    default:
      return [typeElement, costElement, marginElement, percentageElement];
  }
};

export const getTotalFreight = (quoteVersionData: IQuoteVersionData) => {
  if (!quoteVersionData) {
    return undefined;
  }

  switch (quoteVersionData.freightStatus) {
    case FreightStatus.OMITTED:
    case FreightStatus.PREPAID_AND_CHARGE:
      return undefined;
    default:
      return quoteVersionData.freightPrice;
  }
};

export const getDefaultValues = (quoteVersionData: IQuoteVersionData) => {
  if (!quoteVersionData) {
    return {};
  }

  return {
    freightStatus: quoteVersionData.freightStatus,
    freightPriceType: quoteVersionData.freightPrice
      ? FreightPriceType.PRICE
      : FreightPriceType.PERCENTAGE,
    freightPrice: quoteVersionData.freightPrice,
    freightCost: quoteVersionData.freightCost,
    freightPercentage: quoteVersionData.freightPercentage * 100,
    freightNote: quoteVersionData.freightNote,
    freightMargin: PricingHelper.computeAndFormatGpm(
      quoteVersionData.freightCost,
      quoteVersionData.freightPrice
    ),
  };
};
